<template>
  <header class="app-chrome">
    <a class="skip-link" href="#workspace-content">跳到主内容</a>
    <div class="chrome-inner">
      <router-link class="chrome-brand" to="/" aria-label="返回 LabDemo 首页">
        <span class="chrome-brand-mark" aria-hidden="true">Q</span>
        <span class="chrome-brand-copy">
          <strong>Qone Lab</strong>
          <small>Workspace</small>
        </span>
      </router-link>

      <div class="route-context" aria-live="polite">
        <span>{{ currentEntry.groupLabel }} <i aria-hidden="true">/</i> {{ currentEntry.code }}</span>
        <strong>{{ currentEntry.navTitle || currentEntry.title }}</strong>
      </div>

      <div class="chrome-actions">
        <router-link class="chrome-home" to="/">
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M4 10.8 12 4l8 6.8v8.4a.8.8 0 0 1-.8.8H15v-5H9v5H4.8a.8.8 0 0 1-.8-.8z" />
          </svg>
          <span>工作台</span>
        </router-link>

        <details ref="switcher" class="route-switcher">
          <summary>
            <span>切换实验</span>
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m7 9 5 5 5-5" /></svg>
          </summary>
          <div class="route-menu">
            <section v-for="group in groups" :key="group.id">
              <p>{{ group.label }}</p>
              <router-link
                v-for="entry in group.entries"
                :key="entry.to"
                :to="entry.to"
                :class="{ active: entry.to === $route.path }"
                @click="closeSwitcher"
              >
                <span>{{ entry.code }}</span>
                <strong>{{ entry.navTitle || entry.title }}</strong>
              </router-link>
            </section>
          </div>
        </details>

        <nav class="route-stepper" aria-label="实验页切换">
          <router-link :to="previousEntry.to" :aria-label="`上一个：${previousEntry.navTitle || previousEntry.title}`">
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m15 18-6-6 6-6" /></svg>
          </router-link>
          <span>{{ String(currentIndex + 1).padStart(2, '0') }} / {{ String(entries.length).padStart(2, '0') }}</span>
          <router-link :to="nextEntry.to" :aria-label="`下一个：${nextEntry.navTitle || nextEntry.title}`">
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m9 18 6-6-6-6" /></svg>
          </router-link>
        </nav>
      </div>
    </div>
  </header>
</template>

<script>
import { workspaceEntries, workspaceGroups } from '../config/workspaceNavigation'

export default {
  name: 'AppChrome',
  data() {
    return {
      entries: workspaceEntries,
      groups: workspaceGroups
    }
  },
  computed: {
    currentIndex() {
      const index = this.entries.findIndex(entry => entry.to === this.$route.path)
      return index < 0 ? 0 : index
    },
    currentEntry() {
      return this.entries[this.currentIndex]
    },
    previousEntry() {
      return this.entries[(this.currentIndex - 1 + this.entries.length) % this.entries.length]
    },
    nextEntry() {
      return this.entries[(this.currentIndex + 1) % this.entries.length]
    }
  },
  watch: {
    '$route.path'() {
      this.closeSwitcher()
    }
  },
  methods: {
    closeSwitcher() {
      if (this.$refs.switcher) this.$refs.switcher.open = false
    }
  }
}
</script>

<style scoped>
.app-chrome {
  position: sticky;
  top: 0;
  z-index: 1000;
  height: var(--lab-chrome-height);
  color: var(--lab-cream);
  background:
    linear-gradient(90deg, rgba(255, 255, 255, 0.035) 1px, transparent 1px),
    linear-gradient(135deg, #101d21, #172b2f 58%, #1e3839);
  background-size: 32px 100%, auto;
  border-bottom: 1px solid rgba(255, 250, 240, 0.12);
  box-shadow: 0 12px 28px rgba(8, 17, 19, 0.22);
}

.skip-link {
  position: fixed;
  top: 8px;
  left: 12px;
  z-index: 1100;
  padding: 10px 14px;
  color: var(--lab-ink);
  background: var(--lab-cream);
  border-radius: 8px;
  transform: translateY(-160%);
  transition: transform 160ms ease;
}

.skip-link:focus {
  transform: translateY(0);
}

.chrome-inner {
  display: grid;
  width: min(1600px, 100%);
  height: 100%;
  margin: 0 auto;
  padding: 0 clamp(14px, 2.2vw, 34px);
  grid-template-columns: auto minmax(180px, 1fr) auto;
  align-items: center;
  gap: clamp(16px, 3vw, 40px);
}

.chrome-brand,
.chrome-home,
.route-stepper a,
.route-switcher summary,
.route-menu a {
  color: inherit;
  text-decoration: none;
}

.chrome-brand {
  display: inline-flex;
  align-items: center;
  gap: 10px;
}

.chrome-brand-mark {
  display: grid;
  width: 34px;
  height: 34px;
  place-items: center;
  color: var(--lab-ink);
  background: var(--lab-amber);
  font-family: Georgia, 'Songti SC', serif;
  font-size: 1.05rem;
  font-weight: 800;
}

.chrome-brand-copy {
  display: grid;
  line-height: 1.05;
}

.chrome-brand-copy strong,
.route-context span,
.route-stepper span,
.route-menu p,
.route-menu a span {
  font-family: Consolas, 'Courier New', monospace;
  letter-spacing: 0.09em;
  text-transform: uppercase;
}

.chrome-brand-copy strong {
  font-size: 0.72rem;
}

.chrome-brand-copy small {
  margin-top: 5px;
  color: rgba(255, 250, 240, 0.56);
  font-size: 0.62rem;
}

.route-context {
  min-width: 0;
  padding-left: clamp(16px, 2.5vw, 34px);
  border-left: 1px solid rgba(255, 250, 240, 0.14);
}

.route-context span {
  display: block;
  margin-bottom: 4px;
  color: var(--lab-amber);
  font-size: 0.62rem;
}

.route-context i {
  padding-inline: 5px;
  color: rgba(255, 250, 240, 0.32);
  font-style: normal;
}

.route-context strong {
  display: block;
  overflow: hidden;
  font-size: 0.94rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.chrome-actions,
.chrome-home,
.route-stepper,
.route-stepper a,
.route-switcher summary {
  display: flex;
  align-items: center;
}

.chrome-actions {
  gap: 10px;
}

.chrome-home,
.route-switcher summary {
  min-height: 40px;
  padding: 0 13px;
  gap: 8px;
  border: 1px solid rgba(255, 250, 240, 0.14);
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.045);
  font-size: 0.76rem;
  font-weight: 700;
  cursor: pointer;
  transition: background 180ms ease, border-color 180ms ease;
}

.chrome-home:hover,
.route-switcher summary:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(233, 162, 59, 0.48);
}

.chrome-home svg,
.route-switcher summary svg,
.route-stepper svg {
  width: 17px;
  height: 17px;
  fill: none;
  stroke: currentColor;
  stroke-width: 1.8;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.route-switcher {
  position: relative;
}

.route-switcher summary {
  list-style: none;
}

.route-switcher summary::-webkit-details-marker {
  display: none;
}

.route-switcher[open] summary {
  color: var(--lab-ink);
  background: var(--lab-amber);
  border-color: var(--lab-amber);
}

.route-switcher[open] summary svg {
  transform: rotate(180deg);
}

.route-menu {
  position: absolute;
  top: calc(100% + 12px);
  right: 0;
  display: grid;
  width: min(420px, calc(100vw - 24px));
  max-height: calc(100vh - 100px);
  overflow-y: auto;
  padding: 14px;
  gap: 14px;
  color: var(--lab-ink);
  background: rgba(255, 250, 240, 0.98);
  border: 1px solid rgba(23, 38, 42, 0.14);
  border-radius: 16px;
  box-shadow: 0 24px 70px rgba(5, 13, 15, 0.34);
}

.route-menu section {
  display: grid;
  gap: 5px;
}

.route-menu p {
  margin: 2px 8px 4px;
  color: #9d592c;
  font-size: 0.62rem;
  font-weight: 800;
}

.route-menu a {
  display: grid;
  padding: 10px 11px;
  grid-template-columns: 70px 1fr;
  align-items: center;
  border-radius: 9px;
  transition: color 160ms ease, background 160ms ease;
}

.route-menu a:hover,
.route-menu a.active {
  color: var(--lab-cream);
  background: var(--lab-ink);
}

.route-menu a span {
  color: #a76031;
  font-size: 0.6rem;
}

.route-menu a:hover span,
.route-menu a.active span {
  color: var(--lab-amber);
}

.route-menu a strong {
  font-size: 0.82rem;
}

.route-stepper {
  height: 40px;
  overflow: hidden;
  border: 1px solid rgba(255, 250, 240, 0.14);
  border-radius: 10px;
}

.route-stepper a {
  width: 38px;
  height: 100%;
  justify-content: center;
  transition: color 160ms ease, background 160ms ease;
}

.route-stepper a:hover {
  color: var(--lab-ink);
  background: var(--lab-amber);
}

.route-stepper span {
  display: grid;
  min-width: 58px;
  height: 100%;
  place-items: center;
  border-inline: 1px solid rgba(255, 250, 240, 0.12);
  color: rgba(255, 250, 240, 0.64);
  font-size: 0.62rem;
}

@media (max-width: 820px) {
  .chrome-inner {
    grid-template-columns: auto minmax(0, 1fr) auto;
    gap: 12px;
  }

  .chrome-brand-copy,
  .chrome-home span,
  .route-stepper span,
  .route-switcher summary span {
    display: none;
  }

  .chrome-home,
  .route-switcher summary {
    width: 40px;
    padding: 0;
    justify-content: center;
  }

  .route-stepper a {
    width: 34px;
  }
}

@media (max-width: 520px) {
  .app-chrome {
    height: var(--lab-chrome-height-mobile);
  }

  .chrome-inner {
    padding-inline: 10px;
  }

  .chrome-brand-mark {
    width: 32px;
    height: 32px;
  }

  .route-context {
    padding-left: 11px;
  }

  .route-context span {
    font-size: 0.54rem;
  }

  .route-context strong {
    font-size: 0.78rem;
  }

  .chrome-actions {
    gap: 6px;
  }

  .chrome-home,
  .route-switcher summary {
    width: 36px;
    min-height: 36px;
  }

  .route-stepper {
    display: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .skip-link,
  .chrome-home,
  .route-switcher summary,
  .route-menu a,
  .route-stepper a {
    transition: none;
  }
}
</style>
